/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-console */
// src/utils/httpRequest.ts

import axios, { Method } from 'axios';
import toast from 'react-hot-toast';
import { getAccessToken, handleTokenRefresh, redirectToLoginOnSessionExpired } from 'src/utils/authService';
import getEnvVars from 'src/config/env';

const { apiUrl } = getEnvVars();

// Create an Axios instance
const axiosInstance = axios.create({
  baseURL: apiUrl,
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: true
});

// Request interceptor to attach the access token to each request
axiosInstance.interceptors.request.use(
  async (config) => {
    const accessToken = getAccessToken();
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error?.config;

    if (!error.response) {
      toast.error('Unable to connect to the server. Please try again later.');
      return Promise.reject({
        message: 'Network error. Please check your internet connection.',
        type: 'NETWORK_ERROR'
      });
    }

    const statusCode = error.response.status;

    // Handle 401 Unauthorized errors (token expiration)
    if (statusCode === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        // Attempt token refresh using the cookie-stored refresh token
        const newAccessToken = await handleTokenRefresh(); // handleTokenRefresh uses cookies internally
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`; // Optional, in case manual header is needed
        return await axiosInstance(originalRequest);
      } catch (refreshError) {
        redirectToLoginOnSessionExpired();
        return Promise.reject(refreshError);
      }
    }

    // 403 Forbidden errors
    if (statusCode === 403) {
      // toast.error('You are not authorized to access this resource.');
      return Promise.reject(error);
    }

    // Handle 500+ Server errors
    if (statusCode >= 500) {
      toast.error('Server error. Please try again later.');
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

/**
 * Makes an HTTP request using Axios.
 * @param method - The HTTP method (e.g., "GET", "POST").
 * @param endpoint - The API endpoint (relative to the base URL).
 * @param data - The request payload.
 * @param headers - Optional headers to include in the request.
 * @returns The response data from the API.
 */
export const httpRequest = async (
  method: Method,
  endpoint: string,
  data: any = {},
  headers: any = {}
) => {
  try {
    const response = await axiosInstance({
      method,
      url: `${apiUrl}${endpoint}`,
      data,
      headers
    });

    // If the response is empty, handle it gracefully (avoid error propagation)
    if (response.data === null || response.data === undefined) {
      return {}; // Return an empty object for empty responses
    }
    return response.data;
  } catch (error: any) {
    if (error.response) {
      // Handle HTTP error responses
      console.error(`HTTP error: ${error.response.status}`);
      return (
        error.response.data || {
          message: 'An error occurred during the request'
        }
      );
    } else {
      // Handle network or other errors
      console.error('Network error or unable to connect to the server.');
      return {
        message: 'Unable to connect to the server. Please try again later.',
        type: 'NETWORK_ERROR'
      };
    }
  }
};
